import * as React from 'react';
import {useState, useEffect} from "react";
import AddIcon from '@mui/icons-material/Add';
import {DataGrid, GridToolbarExport} from '@mui/x-data-grid';
import { Grid, TextField, FormControl, InputLabel, Select, MenuItem, Button, Box } from '@mui/material';
import {useNavigate} from 'react-router-dom'
import axios from "axios";
import {logout, getLocalToken} from '../utils';
import translations from '../common/translation/translations';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const columns = [
    {field: 'last_name', headerName: 'Nachname', sortable: false, flex: 0.2},
    {field: 'first_name', headerName: 'Vorname', sortable: false, flex: 0.2},
    {field: 'family_count', headerName: 'Familienmitglieder', sortable: false, flex: 0.1},
    {field: 'phone_number', headerName: 'Telefon', sortable: false, flex: 0.3},
    {field: 'email', headerName: 'Email', sortable: false, flex: 0.3},
    {field: 'created', headerName: 'Erstellt', sortable: false, flex: 0.2, hide: true},
    {
        field: 'insurer',
        headerName: 'Versicherer',
        sortable: false,
        flex: 0.3,
        hide: true,
        renderCell: (params) => {
            return params.row?.snapshot?.insurer_name || 'N/A';
        }
    },
    {
        field: 'option',
        headerName: 'Option',
        sortable: false,
        flex: 0.3,
        hide: true,
        renderCell: (params) => {
            return params.row?.snapshot?.tariff_name || 'N/A';
        }
    },
    {
        field: 'status',
        headerName: 'Status',
        sortable: false,
        flex: 0.3,
        hide: true,
        renderCell: (params) => {
            const status = params.value?.toLowerCase() || '';
            let style = {};

            if (status === 'active') {
                style = {color: 'green', fontWeight: 'bold'};
            }
            const translatedStatus = translations['de']?.statuses?.[status] || params.value;
            return <span style={style}>{translatedStatus}</span>;
        }
    }
];

export default function UserPlansTable() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [pageSize, setPageSize] = useState(25);
    const [searchPhrase, setSearchPhrase] = useState('');
    const [currTblPage, setCurrTblPage] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [rows, setRows] = useState([]);
    const [totalRows, setTotalRows] = useState(0);

    const [statusFilter, setStatusFilter] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const token = getLocalToken();

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchPhrase);
            setCurrTblPage(0); //reset to first page when typing a new search
        }, 300);
        return () => clearTimeout(handler);
    }, [searchPhrase]);

    useEffect(() => {
        const offset = currTblPage * pageSize;
        const limit = pageSize;

        let query = `?offset=${offset}&limit=${limit}`;
        if (debouncedSearch) query += `&search=${encodeURIComponent(debouncedSearch)}`;
        if (statusFilter && statusFilter !== 'all') query += `&status=${encodeURIComponent(statusFilter)}`;
        if (startDate) query += `&createdFrom=${startDate}`;
        if (endDate) query += `&createdTo=${endDate}`;

        setLoading(true);
        axios.get(`/admin/api/v1/users/plans${query}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((res) => {
                if (res?.status === 200 && res.data?.payload) {
                    setRows(res.data.payload.items);
                    setTotalRows(Number(res.data.payload.items[0]?.total_count || 0));
                }
            })
            .catch(err => {
                console.error(err);
                if (err.response?.status === 401) logout();
            })
            .finally(() => setLoading(false));

    }, [currTblPage, pageSize, debouncedSearch, token, statusFilter, startDate, endDate]);

    return (
        <React.Fragment>
            <Box mb={2}>
                <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Grid
                        item
                        xs={12}
                        md
                        container
                        spacing={2}
                        alignItems="center"
                        justifyContent="flex-start"
                    >
                        <Grid item xs={12} sm={6} md="auto">
                            <TextField
                                label="Suchen..."
                                variant="standard"
                                size="small"
                                fullWidth
                                value={searchPhrase}
                                onChange={(e) => setSearchPhrase(e.target.value)}
                                sx={{ minWidth: 200 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md="auto">
                            <FormControl variant="standard" size="small" fullWidth sx={{ minWidth: 150 }}>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <MenuItem value="all">Alle</MenuItem>
                                    <MenuItem value="pending_docs">Ausstehende Dokumente</MenuItem>
                                    <MenuItem value="in_review">In Überprüfung</MenuItem>
                                    <MenuItem value="active">Aktiv</MenuItem>
                                    <MenuItem value="canceled">Abgesagt</MenuItem>
                                    <MenuItem value="expired">Abgelaufen</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md="auto">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Von"
                                    value={startDate ? dayjs(startDate) : null}
                                    onChange={(newValue) => setStartDate(newValue ? newValue.format('YYYY-MM-DD') : '')}
                                    format="DD-MM-YYYY"
                                    slotProps={{
                                        textField: {
                                            variant: "standard",
                                            fullWidth: true,
                                            size: "small",
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>

                        {/* End Date */}
                        <Grid item xs={12} sm={6} md="auto">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Bis"
                                    value={endDate ? dayjs(endDate) : null}
                                    onChange={(newValue) => setEndDate(newValue ? newValue.format('YYYY-MM-DD') : '')}
                                    format="DD-MM-YYYY"
                                    slotProps={{
                                        textField: {
                                            variant: "standard",
                                            fullWidth: true,
                                            size: "small",
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md="auto"
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Button
                            variant="text"
                            onClick={() => navigate('/create-new-user')}
                            startIcon={<AddIcon />}
                            sx={{
                                whiteSpace: 'nowrap',
                                fontWeight: 500,
                                color: 'primary.main',
                            }}
                        >
                            Neu erstellen
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            <DataGrid
                autoHeight={true}
                style={{border: '0', minWidth: 100}}
                getEstimatedRowHeight={() => 100}
                getRowHeight={() => 'auto'}
                disableColumnMenu
                pageSize={pageSize}
                initialState={{
                    pagination: {paginationModel: {pageSize: 100}},
                }}
                pageSizeOptions={[25, 50, 100]}
                paginationMode="server"
                rowCount={totalRows}
                pagination
                /* getRowId={(row) => row.planId}*/
                rows={rows}
                columns={columns}
                disableSelectionOnClick
                loading={loading}
                onPaginationModelChange={(val) => {
                    setPageSize(val.pageSize);
                    setCurrTblPage(val.page)
                }}
                sx={{
                    '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {
                        py: 1,
                    },
                    '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {
                        py: '15px',
                    },
                    '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': {
                        py: '22px',
                    },
                }}
                onRowClick={(params) => {
                    navigate(`/user-plans/details/${params.row.id}`, {state: params.row});
                }}
                onSelectionModelChange={(ids) => {
                    const selectedIDs = new Set(ids);
                    const selectedRowData = rows.filter((row) =>
                        selectedIDs.has(row.id.toString())
                    );

                }}
            />
        </React.Fragment>
    );
}